import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, FalhaApi, type ClienteApi, type UsuarioResumoApi, type VistoriaResumoApi } from "../api";
import { Layout } from "../componentes/Layout";
import { PillStatusVistoria } from "../componentes/Pill";
import { useSessao } from "../estado/sessao";

export function Vistorias() {
  const { usuario } = useSessao();
  const admin = usuario?.papel === "ADMIN";
  const [vistorias, setVistorias] = useState<VistoriaResumoApi[] | null>(null);
  // Lista à parte: vistoria excluída (exclusão lógica) nunca aparece nas outras abas, então
  // precisa da própria busca — só ADMIN carrega, e só quando entra na aba.
  const [vistoriasExcluidas, setVistoriasExcluidas] = useState<VistoriaResumoApi[] | null>(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [filtro, setFiltro] = useState<"EM_ANDAMENTO" | "REALIZADAS" | "TODAS" | "EXCLUIDAS">("EM_ANDAMENTO");
  // Período opcional (vazio = sem filtro de data). Comparação por string funciona porque
  // dataVistoria vem sempre como "AAAA-MM-DD..." (meia-noite UTC), mesma convenção do resto do app.
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");

  function carregar() {
    api
      .listarVistorias()
      .then(({ vistorias }) => setVistorias(vistorias))
      .catch(() => setVistorias([]));
  }

  useEffect(carregar, []);

  useEffect(() => {
    if (!admin) return;
    api.listarVistorias({ excluidas: true }).then(({ vistorias }) => setVistoriasExcluidas(vistorias)).catch(() => setVistoriasExcluidas([]));
  }, [admin]);

  function noPeriodo(vistoria: VistoriaResumoApi) {
    const data = vistoria.dataVistoria.slice(0, 10);
    return (!dataInicio || data >= dataInicio) && (!dataFim || data <= dataFim);
  }

  const carregando = filtro === "EXCLUIDAS" ? vistoriasExcluidas === null : vistorias === null;
  const emAndamento = (vistorias ?? []).filter((v) => v.status === "EM_ANDAMENTO" && noPeriodo(v));
  const realizadas = (vistorias ?? []).filter((v) => v.status !== "EM_ANDAMENTO" && noPeriodo(v));
  const excluidas = (vistoriasExcluidas ?? []).filter(noPeriodo);
  const visiveis =
    filtro === "EXCLUIDAS" ? excluidas :
    filtro === "TODAS" ? (vistorias ?? []).filter(noPeriodo) :
    filtro === "EM_ANDAMENTO" ? emAndamento :
    realizadas;
  const textoVazio =
    filtro === "EM_ANDAMENTO" ? "Nenhuma vistoria em andamento no período." :
    filtro === "REALIZADAS" ? "Nenhuma vistoria realizada no período." :
    filtro === "EXCLUIDAS" ? "Nenhuma vistoria excluída no período." :
    "Nenhuma vistoria no período.";

  return (
    <Layout>
      <main className="container">
        <div className="cabecalho-flex"><div><h2>Vistorias</h2><p className="texto-suave">Acompanhe as visitas em andamento e consulte o histórico realizado.</p></div></div>
        <div className="filtro-periodo">
          <label htmlFor="vist-inicio">De</label>
          <input id="vist-inicio" type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
          <label htmlFor="vist-fim">Até</label>
          <input id="vist-fim" type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          {(dataInicio || dataFim) && (
            <button type="button" onClick={() => { setDataInicio(""); setDataFim(""); }}>
              Limpar
            </button>
          )}
        </div>

        <div className="controle-segmentado filtro-vistorias" aria-label="Filtro de vistorias">
          <button className={filtro === "EM_ANDAMENTO" ? "ativo" : ""} onClick={() => setFiltro("EM_ANDAMENTO")}>Em andamento ({emAndamento.length})</button>
          <button className={filtro === "REALIZADAS" ? "ativo" : ""} onClick={() => setFiltro("REALIZADAS")}>Realizadas ({realizadas.length})</button>
          <button className={filtro === "TODAS" ? "ativo" : ""} onClick={() => setFiltro("TODAS")}>Todas ({emAndamento.length + realizadas.length})</button>
          {admin && <button className={filtro === "EXCLUIDAS" ? "ativo" : ""} onClick={() => setFiltro("EXCLUIDAS")}>Excluídas ({excluidas.length})</button>}
        </div>

        {filtro !== "EXCLUIDAS" && (mostrarForm ? (
          <FormularioVistoriaAvulsa admin={admin} aoCancelar={() => setMostrarForm(false)} />
        ) : (
          <button className="botao botao-primario" onClick={() => setMostrarForm(true)}>
            + Nova vistoria avulsa
          </button>
        ))}

        {carregando ? (
          <p style={{ marginTop: 16 }}>Carregando…</p>
        ) : visiveis.length === 0 ? (
          <p style={{ marginTop: 16, color: "var(--texto-suave)" }}>{textoVazio}</p>
        ) : (
          <div className="cartao" style={{ padding: 0, marginTop: 16 }}>
            {visiveis.map((v) => (
              <Link key={v.id} to={`/vistorias/${v.id}`} className="item-lista">
                <span className="item-lista-eyebrow">🏢 Vistoria</span>
                <div className="item-lista-titulo">{v.cliente.nome}</div>
                <div className="item-lista-meta">
                  <span>{v.atribuidoA.nome}</span>
                  <span>{new Date(v.dataVistoria).toLocaleDateString("pt-BR", { timeZone: "UTC" })}</span>
                  {v.excluidaEm ? (
                    <span style={{ color: "var(--erro)" }}>
                      Excluída em {new Date(v.excluidaEm).toLocaleDateString("pt-BR")}{v.excluidaPor ? ` por ${v.excluidaPor.nome}` : ""}
                    </span>
                  ) : (
                    <PillStatusVistoria status={v.status} />
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </Layout>
  );
}

function FormularioVistoriaAvulsa({ admin, aoCancelar }: { admin: boolean; aoCancelar: () => void }) {
  const navegar = useNavigate();
  const [clientes, setClientes] = useState<ClienteApi[] | null>(null);
  const [usuarios, setUsuarios] = useState<UsuarioResumoApi[] | null>(null);
  const [clienteId, setClienteId] = useState("");
  const [vistoriadorId, setVistoriadorId] = useState("");
  const [dataVistoria, setDataVistoria] = useState(() => new Date().toISOString().slice(0, 10));
  const [responsavelNome, setResponsavelNome] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.listarClientes().then(({ clientes }) => setClientes(clientes)).catch(() => setClientes([]));
    // Só ADMIN escolhe o vistoriador — quem não é admin sempre cria a vistoria pra si mesmo
    // (o backend já assume isso por padrão quando vistoriadorId não vem no corpo).
    if (admin) api.listarUsuarios().then(({ usuarios }) => setUsuarios(usuarios)).catch(() => setUsuarios([]));
  }, [admin]);

  const opcoesCliente = (clientes ?? []).map((c) => ({
    id: c.id,
    rotulo: c.administradora ? `${c.nome} (${c.administradora})` : c.nome,
  }));

  async function aoEnviar(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      const { vistoria } = await api.criarVistoria({ clienteId, vistoriadorId: admin ? vistoriadorId : undefined, dataVistoria, responsavelNome });
      navegar(`/vistorias/${vistoria.id}`);
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao criar vistoria");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form className="cartao" style={{ padding: 16, marginTop: 12 }} onSubmit={aoEnviar}>
      {erro && (
        <div className="alerta alerta-erro" role="alert">
          {erro}
        </div>
      )}

      <label htmlFor="v-cliente">Cliente</label>
      <select id="v-cliente" value={clienteId} onChange={(e) => setClienteId(e.target.value)} required>
        <option value="" disabled>
          Selecione…
        </option>
        {opcoesCliente.map((c) => (
          <option key={c.id} value={c.id}>
            {c.rotulo}
          </option>
        ))}
      </select>

      {admin && (
        <>
          <label htmlFor="v-vistoriador">Vistoriador responsável</label>
          <select id="v-vistoriador" value={vistoriadorId} onChange={(e) => setVistoriadorId(e.target.value)} required>
            <option value="" disabled>
              Selecione…
            </option>
            {(usuarios ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </>
      )}

      <label htmlFor="v-data">Data da vistoria</label>
      <input
        id="v-data"
        type="date"
        value={dataVistoria}
        onChange={(e) => setDataVistoria(e.target.value)}
        required
      />

      <label htmlFor="v-responsavel">Responsável pela vistoria (presente no local, opcional)</label>
      <input
        id="v-responsavel"
        value={responsavelNome}
        onChange={(e) => setResponsavelNome(e.target.value)}
      />

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="botao botao-primario" style={{ marginTop: 0 }} type="submit" disabled={salvando}>
          {salvando ? "Criando…" : "Iniciar vistoria"}
        </button>
        <button className="botao botao-texto" type="button" onClick={aoCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
